import { AtSymbolIcon, ClockIcon, TagIcon } from "@heroicons/react/outline";
import dayjs from "dayjs";
import React from "react";
import { CircleIcon } from "@components/library/Icons";
import { UNSET_FEEDBACK } from "lib/constants";
import { getFileFromLog, Log } from "@/types/app/log";
import { HumanEvaluator } from "@/types/app/evaluator";

interface Props {
  logs: Log[];
  humanEvaluators: HumanEvaluator[];
}

const FeedbackDetails = ({ logs, humanEvaluators }: Props) => {
  const sortedFeedbacks = logs
    .sort((a, b) => (a.created_at.isAfter(b.created_at) ? -1 : 1))
    .map((evaluatorLog) => {
      const humanEvaluator = humanEvaluators.filter(
        (humanEvaluator) => humanEvaluator.version_id === getFileFromLog(evaluatorLog).version_id,
      );

      if (humanEvaluator.length === 0) {
        return {
          evaluatorLog,
          humanEvaluator: null,
        };
      }

      return {
        evaluatorLog,
        humanEvaluator: humanEvaluator[0],
      };
    });

  const filteredLogs =
    (sortedFeedbacks.length > 0 && sortedFeedbacks.filter(({ humanEvaluator }) => humanEvaluator !== null)) || [];

  return (
    <div className="grid grid-cols-[auto_auto_auto_auto] gap-x-12 gap-y-16 text-13-16 text-oldgray-800">
      <TableHeader Icon={ClockIcon}>Timestamp</TableHeader>
      <TableHeader Icon={AtSymbolIcon}>Source</TableHeader>
      <TableHeader Icon={TagIcon}>Type</TableHeader>
      <TableHeader Icon={CircleIcon}>Value</TableHeader>
      {filteredLogs.length > 0 ? (
        filteredLogs.map(({ evaluatorLog, humanEvaluator }) => {
          return (
            <React.Fragment key={evaluatorLog.id}>
              <div className="">{dayjs(evaluatorLog.created_at).local().format("L LT")}</div>
              <div>{evaluatorLog.user || <span className="italic text-oldgray-700">None</span>}</div>
              <div>{humanEvaluator?.spec.return_type}</div>
              <div>
                {!("judgment" in evaluatorLog) ? (
                  <span className="italic text-oldgray-700">None</span>
                ) : humanEvaluator?.spec.return_type === "select" ||
                  humanEvaluator?.spec.return_type === "multi_select" ? (
                  evaluatorLog.judgment === UNSET_FEEDBACK ? (
                    <span className="italic text-oldgray-600">None</span>
                  ) : (
                    evaluatorLog.judgment
                  )
                ) : (
                  evaluatorLog.judgment
                )}
              </div>
            </React.Fragment>
          );
        })
      ) : (
        <div className="pt-4 text-center italic text-oldgray-700" style={{ gridColumn: "1/-1" }}>
          No feedback received
        </div>
      )}
    </div>
  );
};

export default FeedbackDetails;

interface TableHeaderProps {
  Icon: (props: React.ComponentProps<"svg">) => JSX.Element;
}

const TableHeader = ({ Icon, children }: React.PropsWithChildren<TableHeaderProps>) => {
  return (
    <div className="flex items-center gap-x-6 pb-6 font-bold">
      <Icon className="h-12 w-12 text-oldgray-600" />
      {children}
    </div>
  );
};
